import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Checkbox,
  Typography,
  Box
} from '@mui/material';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';

function ConfirmationModal({ open, handleClose, handleConfirm, orders = [], setFinalOrders, userInfo=[] }) {
  const [timeRemaining, setTimeRemaining] = React.useState(10);
  const [isFullScreen, setIsFullScreen] = React.useState(false);
  const [selectedTab, setSelectedTab] = React.useState('');
  const [washTradeSelections, setWashTradeSelections] = React.useState({});

  React.useEffect(() => {
    if (open && timeRemaining > 0) {
      const timer = setInterval(() => {
        setTimeRemaining((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [open, timeRemaining]);

  
  // Add this function to check if any transactions have wash sales
  const hasWashSaleTransactions = React.useMemo(() => {
    return orders.some(order => 
      order.transactions?.some(transaction => transaction.isWash === true)
    );
  }, [orders]);

  // Get unique strategies for tabs
  const strategies = React.useMemo(() => {
    const uniqueStrategies = [...new Set(orders.map(order => order?.strategy?.name).filter(Boolean))];
    return uniqueStrategies.length > 0 ? uniqueStrategies : [];
  }, [orders]);

  // Set the first strategy as default selected tab if no tab is selected
  React.useEffect(() => {
    if (strategies.length > 0 && !selectedTab) {
      setSelectedTab(strategies[0]);
    }
  }, [strategies, selectedTab]);

  if (!orders || orders.length === 0) {
    return (
      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>Confirm Orders</DialogTitle>
        <DialogContent>
          <Typography>No orders to display</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  }

  const handleWashTradeChange = (orderId, assetSymbol) => {
    const key = `${orderId}-${assetSymbol}`;
    setWashTradeSelections(prev => ({
      ...prev,
      [key]: !prev[key]
    }));

    setFinalOrders(prevOrders => 
      prevOrders.map(order => {
        if (order.id === orderId) {
          return {
            ...order,
            transactions: order.transactions.map(transaction => {
              if (transaction.asset.symbol === assetSymbol) {
                return {
                  ...transaction,
                  isWash: !washTradeSelections[key]
                };
              }
              return transaction;
            })
          };
        }
        return order;
      })
    );
  };

  const handleConfirmClick = () => {
    // Create the final payload structure matching the expected format
    const payload = {
      items: orders.map(order => {
        // Create base order data
        const orderData = {
          type: order.type,
          orderIds: [order.id], // Array of order IDs
          orderItems: order.transactions.map(transaction => {
            const item = {
              symbol: transaction.asset.symbol,
              isWashTrade: transaction.isWash
            };
            
            // Only add price when it's not null
            if (order.type !== 'MKT' && transaction.price !== null && transaction.price !== undefined) {
              item.price = parseFloat(transaction.price);
            }
            
            return item;
          })
        };

        // Add startTime and endTime only for TWAP orders
        if (order.type === 'TWAP') {
          orderData.startTime = order.startTime;
          orderData.endTime = order.endTime;
        }

        return orderData;
      }),
      isPreview: false
    };
    // Call the parent's handleConfirm with the formatted payload
    handleConfirm(payload);
  };

  const groupByInvestor = (order) => {
    if (!order?.transactions) return {};
    
    // First, try to find user info from the userInfo array
    const matchingUserInfo = userInfo.find(info => 
      info?.userRole?.user?.userInfo && info?.userRole?.id === order?.userRole?.id
    );
    
    let userId = '-';
    
    if (matchingUserInfo?.userRole?.user?.userInfo) {
      const { firstName, lastName } = matchingUserInfo.userRole.user.userInfo;
      const fullName = `${firstName || ''} ${lastName || ''}`.trim();
      userId = fullName || matchingUserInfo.userRole.user.email || '-';
    }
    // Check if userRole has userInfo directly (alternative structure)
    else if (order?.userRole?.userInfo) {
      const { firstName, lastName } = order.userRole.userInfo;
      const fullName = `${firstName || ''} ${lastName || ''}`.trim();
      userId = fullName || order.userRole.email || '-';
    }
    // Check if userRole has user property with userInfo
    else if (order?.userRole?.user?.userInfo) {
      const { firstName, lastName } = order.userRole.user.userInfo;
      const fullName = `${firstName || ''} ${lastName || ''}`.trim();
      userId = fullName || order.userRole.user.email || '-';
    }
    // Check if userRole has user property with email
    else if (order?.userRole?.user?.email) {
      userId = order.userRole.user.email;
    }
    // Check if userRole has email directly
    else if (order?.userRole?.email) {
      userId = order.userRole.email;
    }
    
    // For multiple orders, try to find matching order in userInfo
    if (userId === '-') {
      const matchingOrder = userInfo.find(info => 
        info?.userRole?.user?.userInfo && info.id !== order.id
      );
      
      if (matchingOrder?.userRole?.user?.email) {
        userId = matchingOrder.userRole.user.email;
      }
    }
    
    return {
      [userId]: order.transactions
    };
  };

  // Helper function for number formatting
  const formatNumber = (value) => {
    if (!value) return '0.00';
    return Number(value).toFixed(2);
  };

  // Updated getInvestorName function to handle the new data structure
  const getInvestorName = (order) => {
    // First, try to find user info from the userInfo array
    const matchingUserInfo = userInfo.find(info => 
      info?.userRole?.user?.userInfo && info?.userRole?.id === order?.userRole?.id
    );
    
    if (matchingUserInfo?.userRole?.user?.userInfo) {
      const { firstName, lastName } = matchingUserInfo.userRole.user.userInfo;
      const fullName = `${firstName || ''} ${lastName || ''}`.trim();
      return fullName || matchingUserInfo.userRole.user.email || '-';
    }
    
    // Check if userRole has userInfo directly (alternative structure)
    if (order?.userRole?.userInfo) {
      const { firstName, lastName } = order.userRole.userInfo;
      const fullName = `${firstName || ''} ${lastName || ''}`.trim();
      return fullName || order.userRole.email || '-';
    }
    
    // Check if userRole has user property with userInfo
    if (order?.userRole?.user?.userInfo) {
      const { firstName, lastName } = order.userRole.user.userInfo;
      const fullName = `${firstName || ''} ${lastName || ''}`.trim();
      return fullName || order.userRole.user.email || '-';
    }
    
    // Check if userRole has user property with email
    if (order?.userRole?.user?.email) {
      return order.userRole.user.email;
    }
    
    // Check if userRole has email directly
    if (order?.userRole?.email) {
      return order.userRole.email;
    }
    
    // For multiple orders, try to find matching order in userInfo
    const matchingOrder = userInfo.find(info => 
      info?.userRole?.user?.userInfo && info.id !== order.id
    );

    if (matchingOrder?.userRole?.user?.userInfo) {
      const { firstName, lastName } = matchingOrder.userRole.user.userInfo;
      const fullName = `${firstName || ''} ${lastName || ''}`.trim();
      return fullName || matchingOrder.userRole.user.email || '-';
    }

    // Fallback to email or unknown
    return order?.userRole?.user?.email || order?.userRole?.email || '-';
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="lg" 
      fullWidth
      fullScreen={isFullScreen}
      sx={{
        '& .MuiDialog-container': {
          alignItems: 'center',
          justifyContent: 'center',
        },
        '& .MuiDialog-paper': {
          margin: isFullScreen ? 0 : 2,
          maxHeight: isFullScreen ? '100vh' : '90vh',
          width: isFullScreen ? '100%' : '95%',
          maxWidth: isFullScreen ? '100%' : '1350px',
          marginLeft: isFullScreen ? 0 : "10%"
        }
      }}
    >
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        borderBottom: '1px solid #E8E8E8',
        px: 3,
        py: 2
      }}>
        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '18px' }}>
          You're investing in:
        </Typography>
        <IconButton 
          onClick={() => setIsFullScreen(!isFullScreen)}
          sx={{ 
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)'
            }
          }}
        >
          {isFullScreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 0 }}>
        {/* Two-column layout */}
        <Box sx={{ display: 'flex', minHeight: '600px' }}>
          {/* Left Column - Preview trades */}
          <Box sx={{ 
            flex: 1,
            bgcolor: 'white',
            p: 3,
            display: 'flex',
            flexDirection: 'column',
            borderRight: hasWashSaleTransactions ? '1px solid #E8E8E8' : 'none'
          }}>
            {/* Header */}
            <Typography variant="h6" sx={{ fontSize: '16px', fontWeight: 500, mb: 1 }}>
              Preview your trades
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
              Preview all trades under your order:
            </Typography>

            {/* Warning box */}
            <Box sx={{ 
              bgcolor: '#FEF3F2', 
              p: 2.5, 
              mb: 3, 
              borderRadius: 2,
              border: '1px solid #FECDCA'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Box sx={{ 
                  width: 20, 
                  height: 20, 
                  borderRadius: '50%', 
                  bgcolor: '#F04438', 
                  color: 'white', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  fontSize: '12px',
                  mr: 1
                }}>
                  !
                </Box>
                <Typography sx={{ color: '#B42318', fontWeight: 600, fontSize: '14px' }}>
                  Attention!
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ color: '#B42318', mb: 1, fontSize: '14px' }}>
                These prices are valid for {timeRemaining} seconds. Confirm your investment to buy at these prices.
              </Typography>
              <Typography variant="body2" sx={{ color: '#B42318', fontWeight: 500, fontSize: '14px' }}>
                Time Remaining: {timeRemaining} Seconds
              </Typography>
            </Box>

            {/* Strategy Tabs */}
            <Box sx={{ 
              display: 'flex', 
              borderBottom: '2px solid #1976D2',
              mb: 3,
              gap: 0
            }}>
              {strategies.map((strategy, index) => (
                <Box
                  key={strategy}
                  onClick={() => setSelectedTab(strategy)}
                  sx={{
                    px: 3,
                    py: 1.5,
                    cursor: 'pointer',
                    borderBottom: selectedTab === strategy ? '2px solid #1976D2' : '2px solid transparent',
                    color: selectedTab === strategy ? '#1976D2' : '#666',
                    fontWeight: selectedTab === strategy ? 600 : 400,
                    fontSize: '14px',
                    marginBottom: '-2px',
                    bgcolor: selectedTab === strategy ? '#F5F5F5' : 'transparent',
                    '&:first-of-type': {
                      borderTopLeftRadius: '4px',
                    },
                    '&:last-of-type': {
                      borderTopRightRadius: '4px',
                    }
                  }}
                >
                  {strategy}
                </Box>
              ))}
            </Box>

            {/* Data Table */}
            <Box sx={{ flex: 1 }}>
              <Table sx={{ 
                '& .MuiTableCell-root': {
                  borderBottom: '1px solid #E0E0E0',
                  py: 2,
                  px: 2,
                  fontSize: '14px'
                },
                '& .MuiTableHead-root .MuiTableCell-root': {
                  bgcolor: 'transparent',
                  fontWeight: 600,
                  fontSize: '12px',
                  color: '#666',
                  borderBottom: '1px solid #E0E0E0'
                }
              }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Investor</TableCell>
                    <TableCell>Asset</TableCell>
                    <TableCell align="right">Price</TableCell>
                    <TableCell align="right">Quantity</TableCell>
                    <TableCell align="right">Value($)</TableCell>
                    <TableCell align="center">Buy/Sell</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(() => {
                    const filteredOrders = orders.filter(order => order?.strategy?.name === selectedTab);
                    return filteredOrders.map((order, orderIndex) => {
                      const groupedTransactions = groupByInvestor(order);
                      return Object.entries(groupedTransactions).map(([userId, transactions]) => (
                        transactions.map((transaction, tIndex) => (
                          <TableRow key={`${order?.id || orderIndex}-${userId}-${tIndex}`}>
                            {tIndex === 0 && (
                              <TableCell 
                                rowSpan={transactions.length}
                                sx={{ 
                                  verticalAlign: 'middle',
                                  fontWeight: 500,
                                  color: '#333'
                                }}
                              >
                                {getInvestorName(order)}
                              </TableCell>
                            )}
                            <TableCell sx={{ fontWeight: 500 }}>
                              {transaction?.asset?.symbol || 'N/A'}
                            </TableCell>
                            <TableCell align="right" sx={{ color: '#1976D2', fontWeight: 500 }}>
                              ${formatNumber(transaction?.price)}
                            </TableCell>
                            <TableCell align="right">
                              {formatNumber(transaction?.quantity)}
                            </TableCell>
                            <TableCell align="right">
                              ${formatNumber(transaction?.usdtEquivalent)}
                            </TableCell>
                            <TableCell align="center">
                              <Box sx={{ 
                                bgcolor: transaction?.side === 'SELL' ? '#FEF3F2' : '#F0F9FF',
                                color: transaction?.side === 'SELL' ? '#B42318' : '#1976D2',
                                px: 1.5,
                                py: 0.5,
                                borderRadius: 1,
                                fontSize: '12px',
                                fontWeight: 500,
                                display: 'inline-block'
                              }}>
                                {transaction?.side === 'SELL' ? 'Sell' : 'Buy'}
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))
                      ));
                    });
                  })()}
                </TableBody>
              </Table>
            </Box>
          </Box>

          {/* Right Column - Wash Sale Warning - only show if there are wash sale transactions */}
          {hasWashSaleTransactions && (
            <Box sx={{ 
              flex: 1,
              bgcolor: 'white',
              borderRadius: 1,
              boxShadow: '0px 0px 4px rgba(0, 0, 0, 0.05)',
              border: '1px solid #E8E8E8',
              p: 3
            }}>
              <Typography variant="h6" sx={{ fontSize: '16px', fontWeight: 500, mb: 1 }}>
                Beware! Risk of Wash-Sale Trade
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                The following trades under your order meet conditions for "Wash Sale" trade, and may get flagged by the SEC.
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                Select the trades you'd like to proceed with anyway.
              </Typography>

              {orders.map((order, orderIndex) => {
                // Filter wash transactions
                const washTransactions = order?.transactions?.filter(t => t.isWash) || [];
                
                if (washTransactions.length === 0) return null;

                const groupedWashTransactions = {
                  [order?.userRole?.user?.email || '-']: washTransactions
                };
                
                return (
                  <Box key={`wash-${orderIndex}`} sx={{ 
                    bgcolor: '#E1E8EF',
                    borderRadius: 1,
                    border: '1px solid #E8E8E8',
                    overflow: 'hidden'
                  }}>
                    <Typography 
                      sx={{ 
                        p: 2,
                        fontSize: '14px',
                        fontWeight: 500,
                        marginBottom:"-5%"
                      }}
                    >
                      {order?.strategy?.name || `Strategy ${orderIndex + 1}`}
                      {order?.strategy?.custodian && (
                        <span style={{ 
                          fontSize: '12px', 
                          color: '#666', 
                          marginLeft: '8px' 
                        }}>
                          ({order.strategy.custodian})
                        </span>
                      )}
                    </Typography>
                    <Table 
                      size="small" 
                      sx={{ 
                        '& .MuiTableCell-root': {
                          borderColor: '#E8E8E8',
                          py: 2,
                          px: 2,
                          fontSize: '14px',
                          bgcolor: 'white'
                        },
                        '& .MuiTableHead-root .MuiTableCell-root': {
                          bgcolor: '#E1E8EF',
                          fontWeight: 500,
                          borderBottom: '1px solid #E8E8E8'
                        },
                        '& .MuiTableBody-root .MuiTableRow-root:last-child .MuiTableCell-root': {
                          borderBottom: 'none'
                        }
                      }}
                    >
                      <TableHead>
                        <TableRow>
                          {['', 'Investor', 'Asset', 'Price', 'Quantity', 'Value($)'].map((header, index) => (
                            <TableCell 
                              key={header}
                              padding={index === 0 ? "checkbox" : "normal"}
                              sx={{ 
                                ...(index === 1 && { 
                                  width: '15%',
                                  borderRight: '1px solid #E8E8E8'
                                }),
                                ...(index === 2 && { 
                                  width: '15%'
                                }),
                                ...(index > 2 && {
                                  textAlign: 'right'
                                }),
                                fontFamily: 'Helvetica',
                                fontWeight: 700,
                                fontSize: '12px',
                                lineHeight: '16px',
                                letterSpacing: '0.15px',
                                textAlign: index <= 2 ? 'center' : 'right'  // Center for first three columns, right for others
                              }}
                            >
                              {header}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {Object.entries(groupedWashTransactions).map(([userId, transactions]) => (
                          transactions.map((transaction, tIndex) => (
                            <TableRow key={`${order?.id || orderIndex}-wash-${userId}-${tIndex}`}>
                              <TableCell padding="checkbox" sx={{ bgcolor: '#fff' }}>
                                <Checkbox
                                  checked={!transaction.isWash}
                                  onChange={() => handleWashTradeChange(order.id, transaction.asset.symbol)}
                                />
                              </TableCell>
                              {tIndex === 0 && (
                                <TableCell 
                                  rowSpan={transactions.length}
                                  sx={{ 
                                    verticalAlign: 'middle',
                                    bgcolor: '#fff',
                                    borderRight: '1px solid #E8E8E8',
                                    textAlign: 'center',
                                    fontWeight: 500
                                  }}
                                >
                                  {getInvestorName(order)}
                                </TableCell>
                              )}
                              <TableCell sx={{ bgcolor: '#fff', textAlign: 'center' }}>
                                {transaction?.asset?.symbol || 'N/A'}
                              </TableCell>
                              <TableCell align="right" sx={{ bgcolor: '#fff' }}>
                                ${formatNumber(transaction?.price)}
                              </TableCell>
                              <TableCell align="right" sx={{ bgcolor: '#fff' }}>
                                {formatNumber(transaction?.quantity)}
                              </TableCell>
                              <TableCell align="right" sx={{ bgcolor: '#fff' }}>
                                ${formatNumber(transaction?.usdtEquivalent)}
                              </TableCell>
                            </TableRow>
                          ))
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} variant="outlined">
          Cancel
        </Button>
        <Button onClick={handleConfirmClick} variant="contained" color="primary">
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ConfirmationModal;
